import { BOOT_GLOBAL, type BootAnswers } from '@xshop/shared';

// The globals a store page reads while it draws, stood in for on the server for exactly one drawing: the address it
// was asked at, its first answers, empty storage and a document that holds nothing. Drawing is synchronous, so the
// stand-ins are set, used and taken away before anything else in the process runs; effects, which need the real
// browser, never run on the server at all.

// Does nothing, wherever the page would subscribe to or change the browser.
const noop = (): undefined => undefined;

/** Storage with nothing in it, as a first-time shopper's browser has. */
const emptyStorage = { getItem: () => null, setItem: noop, removeItem: noop, key: () => null, clear: noop, length: 0 };

/** An element that answers every question with nothing. */
const blankElement = {
  dataset: {}, style: {}, classList: { add: noop, remove: noop, toggle: noop, contains: () => false },
  setAttribute: noop, removeAttribute: noop, getAttribute: () => null, appendChild: noop, append: noop, addEventListener: noop, removeEventListener: noop
};

/** The globals a drawing may read, by name. */
type Stand = Record<string, unknown>;

/** The screen a drawing is made for, as the API reads it off the shopper's browser: its size and whether it is touched. */
export interface ServerScreen {
  width: number;
  height: number;
  touch: boolean;
}

// Answers one media feature the way that screen would: its width, hover and pointer, and no reduced motion asked for.
function featureMatches(feature: string, screen: ServerScreen): boolean {
  const term = feature.trim().replace(/^\(|\)$/g, '').trim();
  const width = term.match(/^(min|max)-width:\s*(\d+(?:\.\d+)?)px$/);
  if (width) return width[1] === 'min' ? screen.width >= Number(width[2]) : screen.width <= Number(width[2]);
  if (/^hover:\s*hover$/.test(term)) return !screen.touch;
  if (/^pointer:\s*coarse$/.test(term)) return screen.touch;
  if (/^pointer:\s*fine$/.test(term)) return !screen.touch;
  return term === '' || term === 'screen' || term === 'all';
}

// Answers a whole media query for that screen: any of its comma-separated parts, each all of its "and" terms.
export const queryMatches = (query: string, screen: ServerScreen): boolean =>
  query.split(',').some((part) => part.split(/\band\b/).every((feature) => featureMatches(feature, screen)));

// Builds the window a drawing at that address sees, for that screen, with the page's first answers on it.
function standInWindow(url: string, boot: BootAnswers, screen: ServerScreen): Stand {
  const at = new URL(url);
  const location = { href: at.href, origin: at.origin, protocol: at.protocol, host: at.host, hostname: at.hostname, port: at.port, pathname: at.pathname, search: at.search, hash: at.hash };
  const document = {
    documentElement: blankElement, body: blankElement, head: blankElement, cookie: '', referrer: '', visibilityState: 'visible',
    querySelector: () => null, querySelectorAll: () => [], getElementById: () => null, createElement: () => blankElement, addEventListener: noop, removeEventListener: noop
  };
  // Answers a media query for the screen this drawing is for.
  const matchMedia = (media: string) => ({ matches: queryMatches(media, screen), media, addEventListener: noop, removeEventListener: noop, addListener: noop, removeListener: noop });
  const view: Stand = {
    location, document, localStorage: emptyStorage, sessionStorage: emptyStorage, matchMedia, innerWidth: screen.width, innerHeight: screen.height,
    history: { state: null, pushState: noop, replaceState: noop }, addEventListener: noop, removeEventListener: noop,
    performance: { now: () => 0 }, [BOOT_GLOBAL]: boot
  };
  view.self = view;
  view.top = view;
  return view;
}

// Draws with the stand-in globals in place, and takes them away again whatever the drawing did.
export function withServerWindow<T>(url: string, boot: BootAnswers, screen: ServerScreen, draw: () => T): T {
  const view = standInWindow(url, boot, screen);
  const stood: Stand = { window: view, document: view.document, location: view.location, localStorage: view.localStorage, sessionStorage: view.sessionStorage, matchMedia: view.matchMedia };
  const scope = globalThis as unknown as Stand;
  const before = Object.fromEntries(Object.keys(stood).map((name) => [name, scope[name]]));
  Object.assign(scope, stood);
  try {
    return draw();
  } finally {
    for (const [name, value] of Object.entries(before)) {
      if (value === undefined) delete scope[name];
      else scope[name] = value;
    }
  }
}
